import { useQueries } from "@tanstack/react-query";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { Quote, QuotesResponse } from "@chartandgo/shared";
import { fetchQuotes } from "../api/market.js";

interface QuotesContextValue {
  quotes: Record<string, Quote>;
  asOf: number | null;
  isLoading: boolean;
}

const QuotesContext = createContext<QuotesContextValue>({
  quotes: {},
  asOf: null,
  isLoading: false,
});

/**
 * Polls a single batched quotes request for every visible symbol roughly every
 * 15 seconds while the tab is visible. Batching keeps us well within the free
 * provider rate limit no matter how many charts are on screen.
 */
export function QuotesProvider({
  symbols,
  children,
}: {
  symbols: string[];
  children: ReactNode;
}) {
  const uniqueSorted = useMemo(
    () => [...new Set(symbols.map((s) => s.toUpperCase()))].sort(),
    [symbols],
  );

  const chunks = useMemo(() => {
    const result: string[][] = [];
    for (let index = 0; index < uniqueSorted.length; index += 50) {
      result.push(uniqueSorted.slice(index, index + 50));
    }
    return result;
  }, [uniqueSorted]);

  const queries = useQueries({
    queries: chunks.map((chunk) => ({
      queryKey: ["quotes", chunk],
      queryFn: () => fetchQuotes(chunk),
      refetchInterval: 15_000,
      refetchIntervalInBackground: false,
      staleTime: 10_000,
      retry: 1,
    })),
  });

  const value = useMemo<QuotesContextValue>(
    () => {
      const responses = queries
        .map((query) => query.data as QuotesResponse | undefined)
        .filter((response): response is QuotesResponse => Boolean(response));
      return {
        quotes: Object.assign({}, ...responses.map((response) => response.quotes)),
        asOf:
          responses.length > 0
            ? Math.max(...responses.map((response) => response.asOf))
            : null,
        isLoading: queries.some((query) => query.isLoading),
      };
    },
    [queries],
  );

  return (
    <QuotesContext.Provider value={value}>{children}</QuotesContext.Provider>
  );
}

export function useQuote(symbol: string): Quote | undefined {
  const { quotes } = useContext(QuotesContext);
  return quotes[symbol.toUpperCase()];
}

export function useQuotes(): Record<string, Quote> {
  return useContext(QuotesContext).quotes;
}

export function useQuotesMeta() {
  const { asOf, isLoading } = useContext(QuotesContext);
  return { asOf, isLoading };
}
